import { useEffect, useRef, useState } from 'react';
import { ApiError, errorMessage, getAuthToken, resolveReferenceVideo } from '@/lib/api';
import type { FeedbackDraftScope } from '@/lib/types';
import type { ReferenceVideoFieldValues } from '@/features/coach-review/components/ReferenceVideoField';
import { emptyPendingReference, type FeedbackPendingReference } from '../feedbackContent';
import { useFeedbackAuthoringBoundary } from './useFeedbackAuthoringScope';

type Options = {
  scope: FeedbackDraftScope; active: boolean; disabled: boolean;
  initialPending?: FeedbackPendingReference; reason: string;
  onPendingReferenceChange?: (value: FeedbackPendingReference) => void;
  onChange: (patch: Partial<ReferenceVideoFieldValues>) => void;
  onPendingChange: (pending: boolean) => void;
};
const emptyReference: ReferenceVideoFieldValues = { ref_video_provider: '', ref_video_id: '', ref_video_label: '', ref_video_title: '', ref_video_thumb: '' };

/** Transient check state belongs to this retained form. Durable continuation is
 * supplied by integration; this hook never persists commands or raw URLs. */
export function useFeedbackReferenceVideo(options: Options) {
  const boundary = useFeedbackAuthoringBoundary(options.scope);
  const latest = useRef(options); latest.current = options;
  const initial = useRef(options.initialPending ?? emptyPendingReference).current;
  const [pendingActive, setPendingActive] = useState(Object.values(initial).some(Boolean));
  const [url, setUrl] = useState(initial.pending_reference_input);
  const [reason, setReason] = useState(initial.pending_reference_reason);
  const [expanded, setExpanded] = useState(Object.values(initial).some(Boolean));
  const [error, setError] = useState<string | null>(initial.pending_reference_error || null);
  const [checking, setChecking] = useState(false);
  const generation = useRef(0), alive = useRef(true), inFlight = useRef(false), raw = useRef(initial.pending_reference_input), pending = useRef({ ...initial });
  const pendingChanged = (value: boolean) => { setPendingActive(value); latest.current.onPendingChange(value); };
  const report = (patch: Partial<FeedbackPendingReference>) => {
    pending.current = { ...pending.current, ...patch };
    latest.current.onPendingReferenceChange?.({ ...pending.current });
  };
  const showError = (value: string | null) => { setError(value); report({ pending_reference_error: value ?? '' }); if (value) pendingChanged(true); };
  useEffect(() => { pendingChanged(Object.values(initial).some(Boolean)); }, [initial]);
  const editable = () => alive.current && boundary.current() && latest.current.active && !latest.current.disabled;
  const invalidate = () => { generation.current++; inFlight.current = false; setChecking(false); };
  useEffect(() => { alive.current = true; return () => { alive.current = false; generation.current++; }; }, []);
  useEffect(() => {
    if ((!options.active || options.disabled || !boundary.active) && inFlight.current) {
      invalidate(); showError('Check interrupted. Your link is kept; check it again when you resume.');
    }
  }, [options.active, options.disabled, boundary.active]);

  const changeUrl = (value: string) => {
    if (!editable()) return;
    invalidate(); raw.current = value; setUrl(value); setError(null); report({ pending_reference_input: value, pending_reference_error: '' });
    pendingChanged(Object.values(pending.current).some(Boolean));
  };
  const remove = () => {
    if (!editable()) return;
    invalidate(); raw.current = ''; setUrl(''); setError(null); setExpanded(false);
    setReason(''); report({ ...emptyPendingReference }); latest.current.onChange({ ...emptyReference }); pendingChanged(false);
  };
  const replace = () => {
    if (!editable()) return;
    invalidate(); raw.current = ''; setUrl(''); setError(null); setExpanded(true);
    setReason(latest.current.reason); report({ pending_reference_input: '', pending_reference_reason: latest.current.reason, pending_reference_error: '' });
    latest.current.onChange({ ...emptyReference });
    // The retained reason may need a checked replacement or explicit removal.
    pendingChanged(true);
  };
  async function check() {
    if (!editable() || inFlight.current) return;
    const input = raw.current.trim();
    if (!input) { showError('Paste a video link first'); return; }
    if ([...input].length > 2048) { showError('Use a video link of 2,048 characters or fewer.'); return; }
    const request = ++generation.current, token = getAuthToken();
    inFlight.current = true; setChecking(true); setError(null); pendingChanged(true);
    const current = () => editable() && request === generation.current && token === getAuthToken();
    try {
      const { reference_video: video } = await resolveReferenceVideo(input);
      if (!current()) return;
      if (!['youtube', 'vimeo', 'file'].includes(video.provider) || !video.video_id
        || [...video.video_id].length > 2048 || [...video.title].length > 300 || [...(video.thumb ?? '')].length > 500)
        throw new Error('Invalid normalized reference');
      latest.current.onChange({ ref_video_provider: video.provider, ref_video_id: video.video_id,
        ref_video_title: video.title, ref_video_thumb: video.thumb ?? '', ref_video_label: pendingActive ? pending.current.pending_reference_reason : latest.current.reason });
      raw.current = ''; setUrl(''); setReason(''); report({ ...emptyPendingReference }); pendingChanged(false);
    } catch (cause) {
      if (current()) showError(cause instanceof ApiError && cause.status === 400 ? errorMessage(cause) : "Couldn't check that link. Try again.");
    } finally {
      if (request === generation.current && alive.current) { inFlight.current = false; setChecking(false); }
    }
  }
  return { url, reason, pendingActive, expanded, error, checking, authorized: boundary.active, changeUrl, remove, replace, check,
    expand: () => { if (editable()) setExpanded(true); },
    changeReason: (value: string, attached = true) => { if (!editable()) return;
      if (attached) latest.current.onChange({ ref_video_label: value });
      else { setReason(value); report({ pending_reference_reason: value }); pendingChanged(true); } } };
}
